import { store } from "../../redux/store";
import httpService from "../httpService";

const photoAlbumService = {

  async createPhotoAlbum(album: CreatePhotoAlbum) {
    const response = await httpService.post<PhotoAlbum>(`/networks/communities/${album.communityId}/photo-albums`, album);
    if (album.coverPhoto) {
      await this.updatePhotoAlbumCover(album.id, album.coverPhoto);
    }
    return response.data;
  },

  async getPhotoAlbums(
    communityId: number,
    options?: {
      search?: string;
      sortBy?: 'title' | 'date' | 'photoCount';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PhotoAlbum[]> {
    const params = new URLSearchParams({ communityId: communityId.toString() });
    
    if (options?.search) {
      params.append('search', options.search);
    }
    if (options?.sortBy) {
      params.append('sortBy', options.sortBy);
    }
    if (options?.sortOrder) {
      params.append('sortOrder', options.sortOrder);
    }

    const { data } = await httpService.get<PhotoAlbum[]>(`/networks/communities/${communityId}/photo-albums/?${params.toString()}`);
    return data;
  },

  async getPhotoAlbumById(id: string, startIndex: number, endIndex: number): Promise<PhotoAlbumWithItems> {
    const { selectedCommunity } = store.getState().application;
    const { data } = await httpService.get<PhotoAlbumWithItems>(`/networks/communities/${selectedCommunity}/photo-albums/${id}?start=${startIndex}&end=${endIndex}`);
    return data;
  },

  async deletePhotoAlbum(id: string): Promise<void> {
    const { selectedCommunity } = store.getState().application;
    await httpService.delete(`/networks/communities/${selectedCommunity}/photo-albums/${id}`);
  },

  async updatePhotoAlbum(id: string, updatedAlbum: Partial<PhotoAlbum>): Promise<PhotoAlbum> {
    const { selectedCommunity } = store.getState().application;
    const { data } = await httpService.put<PhotoAlbum>(`/networks/communities/${selectedCommunity}/photo-albums/${id}`, updatedAlbum);
    return data;
  },

  async updatePhotoAlbumCover(id: string, file: File): Promise<PhotoAlbumWithItems> {
    const formData = new FormData();
    formData.append('coverPhoto', file);
    
    const { selectedCommunity } = store.getState().application;
    const { data } = await httpService.put<PhotoAlbumWithItems>(`/networks/communities/${selectedCommunity}/photo-albums/${id}/cover`, formData);
    return data;
  },

  async addPhotosToAlbum(albumId: string, photos: File[]): Promise<Photo[]> {
    const formData = new FormData();
    photos.forEach(photo => formData.append('photos', photo));

    const { selectedCommunity } = store.getState().application;
    const { data } = await httpService.post<Photo[]>(`/networks/communities/${selectedCommunity}/photo-albums/${albumId}/photos`, formData);
    return data;
  },

  async deletePhoto(albumId: string, photoId: number): Promise<void> {
    const { selectedCommunity } = store.getState().application;
    await httpService.delete(`/networks/communities/${selectedCommunity}/photo-albums/${albumId}/photos/${photoId}`);
  },

  async getPhotoAlbumZip(albumId: string): Promise<Blob> {
    const { selectedCommunity } = store.getState().application;
    const response = await httpService.get(`/networks/communities/${selectedCommunity}/photo-albums/${albumId}/zip`, {
      responseType: 'blob'
    });
    
    if (response.status !== 200) {
      throw new Error('Failed to download album zip');
    }
    
    return response.data;
  },

  async getTotalItemCountForCommunity(communityId: number): Promise<number> {
    const { data } = await httpService.get<number>(`/networks/communities/${communityId}/photo-albums/items-count`);
    return data;
  },

  async movePhoto(albumId: string, photoId: number, targetAlbumId: string): Promise<void> {
    const { selectedCommunity } = store.getState().application;
    await httpService.put(`/networks/communities/${selectedCommunity}/photo-albums/${albumId}/photos/${photoId}/move`, {
      targetAlbumId
    });
  },

  async deletePhotos(albumId: string, itemIds: number[]): Promise<void> {
    const { selectedCommunity } = store.getState().application;
    await httpService.delete(`/networks/communities/${selectedCommunity}/photo-albums/${albumId}/photos`, {
      data: { itemIds }
    });
  },

  async movePhotos(albumId: string, itemIds: number[], targetAlbumId: string): Promise<void> {
    const { selectedCommunity } = store.getState().application;
    await httpService.put(`/networks/communities/${selectedCommunity}/photo-albums/${albumId}/photos/move`, {
      itemIds,
      targetAlbumId
    });
  }

}

export default photoAlbumService;